import { RideHailingMode } from "../../Orchestrator/Types/Interface/IRideHailing";
import { IShuttleSelection, IShuttleTrip, ITrainEnds } from "../../Orchestrator/Types/Interface/IShuttleBus";

const ms = (iso: string): number => new Date(iso).getTime();

/** Only the trips closest to the train are recommended; the rest drop to "other". */
export const RECOMMENDED_TRIPS_LIMIT = 3;

/**
 * A connection tighter than this is never recommended. Picking the trips
 * closest to the train would otherwise put a zero-minute transfer at the top
 * of the list — the T&C already puts that time on the passenger.
 *
 * 15 matches what the registry serves on int and prelive, so an environment
 * that can't reach the registry behaves like one that can, instead of being
 * twice as strict.
 *
 * The backend owns this number (`transfert_shuttle` in /metadata/registry, read
 * by useMinConnectionMinutes); the constant is the fallback for every caller
 * that has no value yet — first render, a failed read, an environment where the
 * endpoint isn't live. It stays the default of the three functions that take it, so a
 * missed call site degrades to today's behaviour instead of to no buffer.
 */
export const MIN_CONNECTION_MINUTES = 15;

/**
 * Shuttles that have not left yet. `/offers` returns the whole service day, so
 * once a shuttle can be added after the train departs (the booking-ticket
 * appearance period) the morning's buses would otherwise be listed — and
 * recommended — hours after they ran. An unreadable time is kept, not dropped.
 */
export function withoutDepartedTrips(trips: IShuttleTrip[], now: number = Date.now()): IShuttleTrip[] {
    return trips.filter((trip) => !(ms(trip.depDateTime) <= now));
}

/**
 * Departure side (toStation): shuttle ARRIVAL + buffer ≤ train DEPARTURE.
 * Arrival side (fromStation): shuttle DEPARTURE ≥ train ARRIVAL + buffer.
 * Exactly the buffer counts as recommendable; anything tighter is not.
 * Failing this does NOT hide a trip — it lands under "other", still bookable.
 */
export function isRecommendedTrip(
    trip: IShuttleTrip,
    train: ITrainEnds,
    mode: RideHailingMode,
    minConnectionMinutes: number = MIN_CONNECTION_MINUTES
): boolean {
    const bufferMs = minConnectionMinutes * 60 * 1000;
    if (mode === "toStation") {
        return ms(trip.arrDateTime) + bufferMs <= ms(train.departureDateTime);
    }
    return ms(trip.depDateTime) >= ms(train.arrivalDateTime) + bufferMs;
}

/**
 * Same rule expressed in Dubai wall-clock minutes, for the booking-flow modal:
 * that surface has no ISO timestamps to compare, only minutes since midnight.
 * Lives here so it can be unit-tested — the picking logic is the fix, and
 * inside the component nothing could guard it.
 *
 * Returns the trips closest to the train that still clear the buffer. Anything
 * tighter is simply absent from the result; the caller renders it as "other"
 * rather than hiding it. A null train time means the constraint is unknown, so
 * every trip stays eligible.
 */
export function pickRecommendedByMinutes<T>(
    trips: T[],
    minutesOf: (trip: T) => number,
    trainMinutes: number | null,
    position: "before" | "after",
    minConnectionMinutes: number = MIN_CONNECTION_MINUTES
): T[] {
    const spacedFromTrain = trips.filter((trip) => {
        const tripMinutes = minutesOf(trip);
        if (!Number.isFinite(tripMinutes)) return false;
        if (trainMinutes === null) return true;
        return position === "before"
            ? tripMinutes + minConnectionMinutes <= trainMinutes
            : tripMinutes >= trainMinutes + minConnectionMinutes;
    });

    const sorted = spacedFromTrain.sort((a, b) => minutesOf(a) - minutesOf(b));
    return position === "before"
        ? sorted.slice(-RECOMMENDED_TRIPS_LIMIT)
        : sorted.slice(0, RECOMMENDED_TRIPS_LIMIT);
}

/**
 * Feasibility alone is not enough: a whole day of shuttles can clear the train
 * time, and listing all of them as "recommended" makes the section useless.
 * Keep the RECOMMENDED_TRIPS_LIMIT closest to the train — the LAST to arrive
 * before departure (toStation) or the FIRST to leave after arrival
 * (fromStation) — and demote everything else to "other".
 */
export function splitShuttleTrips(
    trips: IShuttleTrip[],
    train: ITrainEnds,
    mode: RideHailingMode,
    minConnectionMinutes: number = MIN_CONNECTION_MINUTES
): { recommended: IShuttleTrip[]; other: IShuttleTrip[] } {
    const timeline = (trip: IShuttleTrip): number =>
        mode === "toStation" ? ms(trip.arrDateTime) : ms(trip.depDateTime);

    const feasible = trips
        .filter((trip) => isRecommendedTrip(trip, train, mode, minConnectionMinutes))
        .sort((a, b) => timeline(a) - timeline(b));

    // Sorted ascending, so the closest to the train sit at the tail on the
    // departure side and at the head on the arrival side. Either slice stays
    // chronological, which is how the panel renders them.
    const recommended =
        mode === "toStation"
            ? feasible.slice(-RECOMMENDED_TRIPS_LIMIT)
            : feasible.slice(0, RECOMMENDED_TRIPS_LIMIT);

    // Keyed on identity, not offerId: one offer can cover two trips, so an
    // offerId-keyed set would filter the twin out of "other" too and the trip
    // would render in neither section. `feasible` holds the caller's own
    // objects, so reference equality is both correct and cheaper.
    const recommendedSet = new Set(recommended);
    const other = trips.filter((trip) => !recommendedSet.has(trip));

    return { recommended, other };
}

/** Live total fare = current booking total + per-pax price × selected passenger count. */
export function computeShuttleTotal(
    bookingTotal: number,
    perPaxPrice: number,
    selectedCount: number
): number {
    return bookingTotal + perPaxPrice * selectedCount;
}

export function minPerPaxPrice(trips: IShuttleTrip[]): number | undefined {
    if (!trips.length) return undefined;
    return Math.min(...trips.map((t) => t.perPaxPrice));
}

/** Confirm is enabled only when a stop, a time, and at least one passenger are selected. */
export function canConfirmShuttle(selection: IShuttleSelection): boolean {
    return !!selection.stop && !!selection.trip && selection.selectedPassengerRefs.length > 0;
}
